/**
 * Automatic backup export (decision 0017): while a project changes, a copy of its .tablo file
 * goes to the backup folder every N minutes (and on "Back up now"), keeping the last K copies
 * of each project. File names: "<title> (<project id>) <YYYY-MM-DD HH.mm>.tablo".
 */
import type { BackupFolder, Platform } from '@elec/platform';

export const BACKUP_PREFS = { enabled: 'backup.enabled', minutes: 'backup.minutes', keep: 'backup.keep' };

export interface BackupSettings {
  enabled: boolean;
  minutes: number;
  keep: number;
}

export function backupSettings(p: Platform): BackupSettings {
  const n = (k: string, d: number, lo: number, hi: number) => {
    const v = Number(p.getPref(k));
    return Number.isFinite(v) && v >= lo && v <= hi ? v : d;
  };
  return {
    enabled: p.getPref(BACKUP_PREFS.enabled) === '1',
    minutes: n(BACKUP_PREFS.minutes, 30, 5, 24 * 60),
    keep: n(BACKUP_PREFS.keep, 10, 1, 100),
  };
}

export function saveBackupSettings(p: Platform, s: BackupSettings): void {
  p.setPref(BACKUP_PREFS.enabled, s.enabled ? '1' : '0');
  p.setPref(BACKUP_PREFS.minutes, String(s.minutes));
  p.setPref(BACKUP_PREFS.keep, String(s.keep));
}

const pad = (n: number) => String(n).padStart(2, '0');
const safe = (s: string) =>
  s
    // eslint-disable-next-line no-control-regex -- control characters are refused in file names
    .replace(/[\\/:*?"<>|\u0000-\u001f]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
    .slice(0, 60);

export function backupName(project: { id: string; meta: { title: string } }, at: Date): string {
  const d = `${at.getFullYear()}-${pad(at.getMonth() + 1)}-${pad(at.getDate())} ${pad(at.getHours())}.${pad(at.getMinutes())}`;
  return `${safe(project.meta.title) || 'Tablo'} (${project.id.slice(0, 8)}) ${d}.tablo`;
}

/** The copies of a project beyond the last `keep`, oldest first (the names sort by date). */
export function backupsToRemove(names: string[], projectId: string, keep: number): string[] {
  const mark = `(${projectId.slice(0, 8)}) `;
  const mine = names
    .filter((n) => n.includes(mark) && n.endsWith('.tablo'))
    .sort((a, b) => {
      const da = a.slice(a.indexOf(mark) + mark.length);
      const db = b.slice(b.indexOf(mark) + mark.length);
      return da.localeCompare(db);
    });
  return mine.slice(0, Math.max(0, mine.length - keep));
}

/** Write one copy and drop the old ones. Returns the file name written. */
export async function backUp(
  folder: BackupFolder,
  project: { id: string; meta: { title: string } },
  bytes: Uint8Array,
  keep: number,
  at = new Date(),
): Promise<string> {
  const name = backupName(project, at);
  await folder.write(name, bytes);
  for (const old of backupsToRemove(await folder.list(), project.id, keep))
    await folder.remove(old).catch(() => undefined);
  return name;
}
